import React, { useState, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import {
  makeStyles,
  FormControl,
  FormHelperText,
  InputLabel,
  FilledInput,
  Typography,
  Link,
  Button,
  Box
} from '@material-ui/core';

import MinimalFormPage from '../layout/MinimalFormPage';
import Alert from '../common/alert';

import {
  forgotPassword,
  selectForgotPwStatus,
  clearForgotPwState
} from '../auth/authSlice';

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    marginTop: theme.spacing(1)
  },
  btn: {
    minWidth: '147px'
  }
}));

const ForgotPasswordPage = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { status, error } = useSelector(selectForgotPwStatus);


  const [ formValues, setFormValues ] = useState({email: ''});
  const [ formErrors, setFormErrors ] = useState({});

  useLayoutEffect(() => {
    return () => {
      dispatch(clearForgotPwState());
    }
  }, []);

  const validations = {
    email: email => {
      if(!email || email.trim() === '') {
        return t('fieldIsRequired', {field: t('forgotPassword:email')});
      }

      return null;
    }
  }

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formValues).forEach(field => {
      const value = formValues[field];
      newErrors[field] = validations[field](value);
    });

    setFormErrors(newErrors);

    if(Object.keys(newErrors).every(field => newErrors[field] === null)) {
      return true;
    }

    return false;
  }

  const handleChange = event => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    setFormValues({
      ...formValues,
      [name]: value
    });

    // Reset errors
    setFormErrors({
      ...formErrors,
      [name]: null
    });

    // Reset possible request error messages
    if(status === 'failed') {
      dispatch(clearForgotPwState());
    }
  };

  const handleSubmit = event => {
    event.preventDefault();

    if(validateForm()) {
      if(status !== 'loading') {
        dispatch(forgotPassword(formValues.email));
      }
    }
  };

  return (
    <MinimalFormPage>

      <Typography variant="body1" align="left">{t('forgotPassword:helper')}</Typography>

      {status === 'failed' ? <Box py={2}><Alert severity="error">{error.message}</Alert></Box> : null}

      {status === 'succeeded' ? <Box py={2}><Alert severity="success">{t('forgotPassword:emailSent')}</Alert></Box> : null}

      <form className={classes.form} onSubmit={handleSubmit}>
        <FormControl variant="filled" margin="normal" error={!!formErrors.email}>
          <InputLabel htmlFor="email">{t('forgotPassword:email')}</InputLabel>
          <FilledInput
            type="email"
            id="email"
            name="email"
            value={formValues.email}
            required
            placeholder={t('forgotPassword:emailPlaceholder')}
            autoComplete="email"
            autoFocus
            onChange={handleChange}
            aria-describedby={formErrors.email ? 'email-error-text' : undefined }
          />
          {formErrors.email ? <FormHelperText id="email-error-text">{formErrors.email}</FormHelperText> : null}
        </FormControl>

        <Box mt={2} mb={3}>
          <Button className={classes.btn} color="primary" variant="contained" type="submit" disabled={status === 'loading'}>{t('forgotPassword:send')}</Button>
        </Box>
      </form>

      <Typography variant="body1" component="p">
        <Link component={RouterLink} to="/login">{t('forgotPassword:signIn')}</Link>
      </Typography>
    </MinimalFormPage>
  );
};

export default ForgotPasswordPage;
